import { useState, useEffect } from 'react'
import { motion, type PanInfo } from 'motion/react'
import { X, CheckCircle, AlertCircle, Reply, Forward, User, Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { EmailBodyView } from '@tendbot/ui'
import { relativeTime } from '@tendbot/ui'
import { getEmailById, type EmailDetailResponse } from '../../shared/api/emails'
import type { DeepLink } from '../../shared/types/navigation'
import type { EmailItem, IncomingEmail, SentEmail } from './api'

function isIncoming(email: EmailItem): email is IncomingEmail {
  return email.direction === 'incoming'
}

function isSent(email: EmailItem): email is SentEmail {
  return email.direction === 'outgoing'
}

function sourceLabel(source: string): string | null {
  if (source === 'gmail') return 'Gmail'
  if (source === 'outlook') return 'Outlook'
  if (source.endsWith('.bot')) return '@tend.bot'
  return null
}

type Props =
  | {
      email: EmailItem
      emailId?: never
      onClose: () => void
      onNavigate?: (tab: string, params?: DeepLink | null) => void
    }
  | {
      email?: never
      emailId: string
      onClose: () => void
      onNavigate?: (tab: string, params?: DeepLink | null) => void
    }

/**
 * Bottom-sheet email detail view. Two modes:
 *   - `email`: full EmailItem (from MailScreen list) — renders metadata badges
 *     immediately, lazy-loads body.
 *   - `emailId`: just an id (e.g. from a briefing inbox row) — shows a loading
 *     shell until `getEmailById` resolves, then renders without the
 *     list-derived badges (importance / fromSelf / source).
 *
 * Title-bar gesture: swipe down to close.
 */
export default function EmailDetailSheet(props: Props) {
  const { t } = useTranslation('mail')
  const { onClose, onNavigate } = props
  const id = 'email' in props && props.email ? props.email.id : props.emailId!

  const [detail, setDetail] = useState<EmailDetailResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)

  // The sheet is keyed on the email id by callers (MailScreen, ChatScreen),
  // so a new id always remounts and re-runs this effect from a fresh state.
  useEffect(() => {
    let cancelled = false
    getEmailById(id)
      .then(d => { if (!cancelled) setDetail(d) })
      .catch(() => { if (!cancelled) setError(true) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [id])

  // ─── Mode A: full EmailItem provided (from MailScreen list) ──────
  const fullEmail = 'email' in props ? props.email : undefined
  const sent = fullEmail ? isSent(fullEmail) : detail?.direction === 'outgoing'
  const incoming = fullEmail && isIncoming(fullEmail)
    ? fullEmail
    : null

  // Derived display fields — prefer list metadata, fall back to detail fetch.
  const subject = fullEmail?.subject ?? detail?.subject ?? ''
  const senderName = fullEmail
    ? (isSent(fullEmail) ? fullEmail.to.name : (fullEmail as IncomingEmail).from.name)
    : (detail?.senderName ?? detail?.fromEmail ?? '')
  const senderEmail = fullEmail
    ? (isSent(fullEmail) ? fullEmail.to.email : (fullEmail as IncomingEmail).from.email)
    : (detail?.fromEmail ?? '')
  const timestamp = fullEmail?.timestamp ?? detail?.receivedAt ?? null

  // Reply / forward only for incoming emails. In id-only mode we infer from
  // detail.direction so newsletters / inbound briefing items still get the
  // action buttons when applicable.
  const canReply = (incoming != null) || (!fullEmail && detail?.direction === 'incoming')
  const replySubject = subject
  const handleReply = () => {
    if (!canReply || !senderEmail) return
    const msg = `Reply to email from ${senderName} <${senderEmail}> about "${replySubject}"`
    onNavigate?.('chat', { type: 'send-message', id, message: msg })
    onClose()
  }
  const handleForward = () => {
    if (!canReply || !senderEmail) return
    const msg = `Forward the email from ${senderName} <${senderEmail}> about "${replySubject}"`
    onNavigate?.('chat', { type: 'send-message', id, message: msg })
    onClose()
  }

  const handleHeaderDragEnd = (_: unknown, info: PanInfo) => {
    const dy = info.offset.y
    const vy = info.velocity.y
    if (dy > 60 || vy > 500) {
      onClose()
    }
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60"
      onClick={onClose}
    >
      <motion.div
        initial={{ y: '100%' }}
        animate={{ y: 0 }}
        exit={{ y: '100%' }}
        transition={{ type: 'spring', damping: 28, stiffness: 300 }}
        onClick={(e) => e.stopPropagation()}
        className="relative w-full bg-charcoal flex flex-col overscroll-contain h-[92dvh] max-w-lg border-t border-gold/20 rounded-t-2xl sm:border sm:rounded-2xl sm:shadow-2xl"
      >
        {/* Drag handle / title bar — swipe down to close */}
        <motion.div
          drag="y"
          dragConstraints={{ top: 0, bottom: 0 }}
          dragElastic={0.4}
          dragMomentum={false}
          onDragEnd={handleHeaderDragEnd}
          className="relative flex items-start justify-between flex-shrink-0 cursor-grab active:cursor-grabbing select-none touch-none pt-4 pb-2 px-3"
        >
          <div className="absolute top-1.5 left-1/2 -translate-x-1/2 h-1 w-10 rounded-full bg-pearl/25" />
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-medium text-ivory truncate">
              {subject || (loading ? '…' : t('emptyState'))}
            </h3>
            {senderName && (
              <p className="text-xs text-pearl/50 mt-0.5">
                {sent ? t('to') : t('from')} {senderName}{senderEmail ? ` <${senderEmail}>` : ''}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="ml-3 p-1.5 rounded-lg text-pearl/60 hover:text-ivory hover:bg-white/5 transition-colors flex-shrink-0"
          >
            <X size={18} />
          </button>
        </motion.div>

        {/* Metadata + timestamp */}
        <div className="flex-shrink-0 px-3">
          {fullEmail && (
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              {sent && (
                <span className="flex items-center gap-1 text-emerald/70 text-xs">
                  <CheckCircle size={13} />
                  {t('delivered')}
                </span>
              )}
              {incoming?.importance === 'high' && (
                <span className="flex items-center gap-1 text-amber/80 text-xs">
                  <AlertCircle size={13} />
                  {t('important')}
                </span>
              )}
              {incoming?.fromSelf && (
                <span className="flex items-center gap-1 text-pearl/50 text-xs">
                  <User size={13} />
                  {t('fromYou')}
                </span>
              )}
              {incoming && (
                <span className="text-xs text-pearl/55 px-1.5 py-0.5 rounded bg-white/5">
                  {sourceLabel(incoming.source) || incoming.source}
                </span>
              )}
            </div>
          )}
          {timestamp && <div className="text-xs text-pearl/60 mb-2">{relativeTime(timestamp)}</div>}
          <div className="h-px bg-gold/10 mb-2" />
        </div>

        {/* Body */}
        <div className="flex-1 flex flex-col min-h-0 px-1 pb-1">
          {loading ? (
            <div className="flex-1 flex items-center justify-center min-h-[160px] text-pearl/50">
              <Loader2 size={18} className="animate-spin" />
            </div>
          ) : detail && !error ? (
            <EmailBodyView
              bodyHtml={detail.bodyHtml}
              bodyText={detail.bodyText}
              title={subject}
            />
          ) : fullEmail ? (
            <div className="flex-1 overflow-y-auto text-sm text-ivory/80 leading-relaxed whitespace-pre-line px-2">
              {fullEmail.preview}
            </div>
          ) : (
            <div className="flex-1 flex items-center justify-center min-h-[160px] text-pearl/50 text-sm">
              {t('emptyState')}
            </div>
          )}
        </div>

        {/* Reply / forward — incoming only */}
        {canReply && onNavigate && (
          <div className="flex gap-2 pt-3 pb-3 border-t border-gold/10 flex-shrink-0 px-3">
            <button
              onClick={handleReply}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-gold/10 text-gold hover:bg-gold/20 transition-colors text-sm font-medium"
            >
              <Reply size={16} />
              {t('reply')}
            </button>
            <button
              onClick={handleForward}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-white/5 text-pearl/70 hover:bg-white/10 transition-colors text-sm font-medium"
            >
              <Forward size={16} />
              {t('forward')}
            </button>
          </div>
        )}
      </motion.div>
    </motion.div>
  )
}
